import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Search } from '../../Components';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import HistoryIcon from '@mui/icons-material/History';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { Link, useNavigate } from 'react-router-dom';
import {
   useGetQuotationsQuery,
   useSendQuotationEmailMutation,
} from '../../Redux/Service/QuotationApi';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { API_URL } from '../../Config';
import Loading from '../../Components/Loading';
import PostAddIcon from '@mui/icons-material/PostAdd';
import axios from 'axios';
import { getAuthToken } from '../../Redux/Service/FetchBaseQuery';
import StyledDashboardLayout from '../style';
import Paper from '@mui/material/Paper';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import QuotationPreviewModal from '../../Components/QuotationPreviewModal';
import QuotationLogsModal from '../../Components/QuotationLogsModal';
import { Tooltip } from '@mui/material';
import moment from 'moment';
import { useModuleHasPermissions } from '../../hooks/useModulePermissions';

function QuotationList() {
   const MySwal = withReactContent(Swal);
   const [loading, setLoading] = useState(true);
   const navigate = useNavigate();
   const [quotationData, setQuotationData] = useState(null);
   const [searchInput, setSearchInput] = useState('');
   const [searchQuery, setSearchQuery] = useState('');
   const [page, setPage] = useState(1);
   const [rowsPerPage, setRowsPerPage] = useState(10);
   const [sort, setSort] = useState({ sortBy: '', orderBy: 'DESC' });
   // menu and discount modal state
   const [anchorEl, setAnchorEl] = useState(null);
   const [menuRow, setMenuRow] = useState(null);
   const [discountOpen, setDiscountOpen] = useState(false);
   const [discounts, setDiscounts] = useState([]);
   const [selectedDiscountIds, setSelectedDiscountIds] = useState([]);
   const [discountLoading, setDiscountLoading] = useState(false);
   const [originalSelectedIds, setOriginalSelectedIds] = useState([]);
   // Add-on modal state
   const [addonOpen, setAddonOpen] = useState(false);
   const [addons, setAddons] = useState([]);
   const [selectedAddonIds, setSelectedAddonIds] = useState([]);
   const [addonLoading, setAddonLoading] = useState(false);
   const [originalSelectedAddonIds, setOriginalSelectedAddonIds] = useState([]);
   // Preview, Send, and Logs modal state
   const [previewOpen, setPreviewOpen] = useState(false);
   const [logsOpen, setLogsOpen] = useState(false);
   const [sendLoading, setSendLoading] = useState(false);
   const [redirectOpen, setRedirectOpen] = useState(false);

   const [sendQuotationEmail] = useSendQuotationEmailMutation();
   const hasPermissions = useModuleHasPermissions('quotation');
   const {
      data: quotations,
      refetch,
      isLoading,
      isError,
      error,
   } = useGetQuotationsQuery({
      limit: rowsPerPage,
      page: page - 1,
      orderBy: sort.orderBy,
      sortBy: sort.sortBy,
      search: searchQuery,
   });

   const openMenuForRow = (event, row) => {
      setAnchorEl(event.currentTarget);
      setMenuRow(row);
   };

   const closeMenu = () => {
      setAnchorEl(null);
      setMenuRow(null);
   };

   const openDiscountModal = async () => {
      // preserve selected row before closing menu
      const currentRow = menuRow;
      // close menu popup but keep the menuRow (quotation) so we can use its id
      setAnchorEl(null);

      // Check if quotation is already accepted by client
      if (currentRow?.clientAccepted) {
         toast.error(
            'Cannot modify services for accepted quotations. Client has already accepted this quotation.',
         );
         return;
      }

      setDiscountLoading(true);
      try {
         // Fetch all available discounts
         const discountRes = await axios.get(`${API_URL}discount/all`, {
            headers: { Authorization: `Bearer ${getAuthToken()}` },
         });

         // Fetch existing quotation discounts (if any)
         let existingDiscounts = [];
         try {
            const quotationDiscountRes = await axios.get(
               `${API_URL}quotation/${currentRow.id}/discounts/public`,
            );
            if (quotationDiscountRes.data.statusCode === 200) {
               existingDiscounts = quotationDiscountRes.data.response.quotationDiscounts || [];
            }
         } catch (quotationErr) {
            console.log('No existing discounts found for this quotation');
         }

         // Map discounts into editable objects and pre-select existing ones
         const fetched = discountRes.data.response.data || [];
         const selectedIds = [];

         const editable = fetched.map((d) => {
            // Check if this discount is already applied to the quotation
            const existingDiscount = existingDiscounts.find((ed) => ed.discount.id === d.id);
            const isSelected = !!existingDiscount;

            if (isSelected) {
               selectedIds.push(d.id);
            }

            return {
               ...d,
               selected: isSelected,
               editedType: existingDiscount ? existingDiscount.type : d.type,
               editedValue: existingDiscount ? existingDiscount.value : d.value,
               originalType: existingDiscount ? existingDiscount.type : d.type,
               originalValue: existingDiscount ? existingDiscount.value : d.value,
               existingQDId: existingDiscount ? existingDiscount.id : null, // store quotationDiscount id
            };
         });

         // Custom (one-off) discounts of this quotation are not in the predefined list
         existingDiscounts
            .filter((ed) => ed.discount?.is_custom)
            .forEach((ed) => {
               selectedIds.push(ed.discount.id);
               editable.push({
                  id: ed.discount.id,
                  name: ed.discount.name,
                  isCustom: true,
                  is_active: true,
                  selected: true,
                  editedType: ed.type,
                  editedValue: ed.value,
                  originalType: ed.type,
                  originalValue: ed.value,
                  originalName: ed.discount.name,
                  existingQDId: ed.id,
               });
            });

         setDiscounts(editable);
         setSelectedDiscountIds(selectedIds);
         setOriginalSelectedIds(selectedIds);
      } catch (err) {
         console.error('Failed to fetch discounts', err);
         setDiscounts([]);
         setSelectedDiscountIds([]);
         toast.error('Failed to load discounts');
      } finally {
         setDiscountLoading(false);
         setDiscountOpen(true);
         // restore the preserved row so applyDiscounts can use it
         if (currentRow) setMenuRow(currentRow);
      }
   };

   const handlePreviewClick = () => {
      console.log('handlePreviewClick - menuRow:', menuRow);
      // Preserve menuRow by storing it before closing the menu
      const currentRow = menuRow;
      setAnchorEl(null);
      // Keep menuRow for modal to use
      if (currentRow) {
         setMenuRow(currentRow);
      }
      setPreviewOpen(true);
   };

   const handleLogsClick = () => {
      const currentRow = menuRow;
      setAnchorEl(null);
      if (currentRow) {
         setMenuRow(currentRow);
      }
      setLogsOpen(true);
   };

   const handleSendClick = async () => {
      const currentRow = menuRow;
      setAnchorEl(null);
      const quotationId = currentRow?.id;
      const getSpecificQuotation = quotationData?.data?.find((q) => q.id === quotationId);
      const createdDate = moment(getSpecificQuotation?.date);
      const diff = createdDate.diff(new Date(), 'days');

      if (diff < 0 && Math.abs(diff) > getSpecificQuotation?.validTimeDay) {
         setRedirectOpen(true);
         return;
      }

      const clientEmail = currentRow?.clientEmail;

      if (!quotationId) {
         toast.error('Quotation not selected');
         return;
      }

      if (!clientEmail) {
         toast.error('Client email not found for this quotation');
         return;
      }

      const result = await MySwal.fire({
         title: 'Send Quotation',
         text: `Are you sure you want to send this quotation to ${clientEmail}?`,
         icon: 'question',
         showCancelButton: true,
         confirmButtonText: 'Send',
         cancelButtonText: 'Cancel',
      });

      if (!result.isConfirmed) return;

      setSendLoading(true);
      try {
         const response = await sendQuotationEmail(quotationId).unwrap();
         if (response?.statusCode === 200) {
            toast.success(response?.message || `Quotation sent to ${clientEmail}`);
            refetch();
         } else {
            toast.error(response?.message || 'Failed to send quotation');
         }
      } catch (err) {
         const message = err?.data?.message || 'Failed to send quotation email';
         toast.error(message);
         console.error('Send email error:', err);
      } finally {
         setSendLoading(false);
      }
   };

   const toggleDiscountSelection = (id) => {
      setDiscounts((prev) => prev.map((d) => (d.id === id ? { ...d, selected: !d.selected } : d)));
      setSelectedDiscountIds((prev) =>
         prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      );
   };

   const handleDiscountTypeChange = (id, newType) => {
      setDiscounts((prev) => prev.map((d) => (d.id === id ? { ...d, editedType: newType } : d)));
   };

   const handleDiscountValueChange = (id, newValue) => {
      setDiscounts((prev) => prev.map((d) => (d.id === id ? { ...d, editedValue: newValue } : d)));
   };

   const addCustomDiscount = () => {
      setDiscounts((prev) => [
         ...prev,
         {
            id: `custom-${Date.now()}`,
            name: '',
            isCustom: true,
            is_active: true,
            selected: true,
            editedType: 'percentage',
            editedValue: '',
            existingQDId: null,
         },
      ]);
   };

   const removeNewCustomDiscount = (id) => setDiscounts((prev) => prev.filter((d) => d.id !== id));

   const handleDiscountNameChange = (id, name) => {
      setDiscounts((prev) => prev.map((d) => (d.id === id ? { ...d, name } : d)));
   };

   // Predefined discounts are sent by id; custom ones by name (the server creates them)
   const toDiscountPayload = (d) => ({
      ...(d.isCustom ? { name: d.name.trim() } : { discountId: d.id }),
      type: d.editedType ?? d.type,
      value: Number(d.editedValue ?? d.value),
   });

   const applyDiscounts = async () => {
      const selectedRows = discounts.filter((d) => d.selected);
      const invalidCustom = selectedRows.find(
         (d) => d.isCustom && (!d.name.trim() || String(d.editedValue).trim() === ''),
      );
      if (invalidCustom) {
         toast.error('Custom discount needs a name and a value');
         return;
      }
      const selected = selectedRows.map(toDiscountPayload);
      if (selected.length === 0 && originalSelectedIds.length === 0) {
         toast.error('Please select at least one discount');
         return;
      }

      if (!menuRow || !menuRow.id) {
         toast.error('Quotation not selected');
         return;
      }

      // Validate selected discounts
      for (const discount of selected) {
         const numValue = Number(discount.value);
         if (isNaN(numValue)) {
            toast.error('Invalid value for discount. Please enter a valid number.');
            return;
         }
         if (discount.type === 'percentage') {
            if (numValue < 0 || numValue > 100) {
               toast.error('Percentage discount must be between 0 and 100');
               return;
            }
         } else if (discount.type === 'fixed') {
            if (numValue < 0) {
               toast.error('Fixed discount amount cannot be negative');
               return;
            }
         }
      }

      setDiscountLoading(true);
      try {
         const currentSelectedIds = discounts.filter((d) => d.selected).map((d) => d.id);
         const addedIds = currentSelectedIds.filter((id) => !originalSelectedIds.includes(id));
         const removedIds = originalSelectedIds.filter((id) => !currentSelectedIds.includes(id));

         // Detect already-selected items whose type or value changed (needs delete + re-add)
         const modifiedIds = originalSelectedIds.filter((id) => {
            if (!currentSelectedIds.includes(id)) return false; // being removed, handled separately
            const item = discounts.find((d) => d.id === id);
            if (!item) return false;
            return (
               String(item.editedType) !== String(item.originalType) ||
               String(item.editedValue) !== String(item.originalValue) ||
               (item.isCustom && item.name.trim() !== item.originalName)
            );
         });

         // First, delete removed/deselected discounts AND modified ones (will be re-added)
         const idsToDelete = [...removedIds, ...modifiedIds];
         if (idsToDelete.length > 0) {
            // find corresponding quotationDiscount ids and delete
            const deletePromises = idsToDelete.map(async (discountId) => {
               const item = discounts.find((d) => d.id === discountId);
               const qdId = item?.existingQDId;
               if (qdId) {
                  try {
                     await axios.delete(`${API_URL}quotation/${menuRow.id}/discounts/${qdId}`, {
                        headers: {
                           Authorization: `Bearer ${getAuthToken()}`,
                        },
                     });
                  } catch (delErr) {
                     console.error('Failed to delete quotation discount', delErr);
                     // continue deleting others
                  }
               }
            });
            await Promise.all(deletePromises);
         }

         // Next, add newly selected discounts AND re-add modified ones
         const idsToAdd = [...addedIds, ...modifiedIds];
         if (idsToAdd.length > 0) {
            const toAdd = discounts
               .filter((d) => idsToAdd.includes(d.id))
               .map(toDiscountPayload);

            const payload = { quotationDiscounts: toAdd };
            const res = await axios.post(`${API_URL}quotation/${menuRow.id}/discounts`, payload, {
               headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${getAuthToken()}`,
               },
            });

            if (!(res?.data?.statusCode === 200)) {
               const msg = res?.data?.message || 'Failed to apply discounts';
               toast.error(msg);
               setDiscountLoading(false);
               return;
            }
         }

         toast.success('Discounts updated successfully');
         setDiscountOpen(false);
         refetch();
      } catch (err) {
         console.error('Error applying discounts', err);
         toast.error('Error updating discounts');
      } finally {
         setDiscountLoading(false);
      }
   };

   const handleDownload = async (quotationId, title) => {
      try {
         const response = await axios.get(`${API_URL}quotation/${quotationId}/download`, {
            responseType: 'blob',
            headers: {
               Authorization: `Bearer ${getAuthToken()}`,
            },
         });

         const url = window.URL.createObjectURL(
            new Blob([response.data], { type: 'application/pdf' }),
         );

         const link = document.createElement('a');
         link.href = url;
         link.setAttribute('download', `${(title || '').replace(/[^\w\- ]+/g, '').trim() || `quotation-${quotationId}`}.pdf`);
         document.body.appendChild(link);
         link.click();

         link.remove();
         window.URL.revokeObjectURL(url);
      } catch (error) {
         console.error(error);
         toast.error('Failed to download PDF');
      }
   };

   const openAddonModal = async () => {
      // preserve selected row before closing menu
      const currentRow = menuRow;
      // close menu popup but keep the menuRow (quotation) so we can use its id
      setAnchorEl(null);

      // Check if quotation is already accepted by client
      if (currentRow?.clientAccepted) {
         toast.error(
            'Cannot modify services for accepted quotations. Client has already accepted this quotation.',
         );
         return;
      }

      setAddonLoading(true);
      try {
         // Fetch all available add-ons
         const addonRes = await axios.get(`${API_URL}addon/all`, {
            headers: { Authorization: `Bearer ${getAuthToken()}` },
         });

         // Fetch existing quotation add-ons (if any)
         let existingAddons = [];
         try {
            const quotationAddonRes = await axios.get(
               `${API_URL}quotation/${currentRow.id}/addons/public`,
            );
            if (quotationAddonRes.data.statusCode === 200) {
               existingAddons = quotationAddonRes.data.response.quotationAddons || [];
            }
         } catch (quotationErr) {
            console.log('No existing services found for this quotation');
         }

         // Map add-ons into editable objects and pre-select existing ones
         const fetched = addonRes.data.response.data || [];
         const selectedIds = [];

         const editable = fetched.map((a) => {
            // Check if this add-on is already applied to the quotation
            const existingAddon = existingAddons.find((ea) => ea.addonService.id === a.id);
            const isSelected = !!existingAddon;

            if (isSelected) {
               selectedIds.push(a.id);
            }

            return {
               ...a,
               selected: isSelected,
               editedType: existingAddon ? existingAddon.type : a.type,
               editedValue: existingAddon ? existingAddon.value : a.value,
               originalType: existingAddon ? existingAddon.type : a.type,
               originalValue: existingAddon ? existingAddon.value : a.value,
               isRegularService: existingAddon ? (existingAddon.isRegularService ?? false) : false,
               originalIsRegularService: existingAddon
                  ? (existingAddon.isRegularService ?? false)
                  : false,
               existingQAId: existingAddon ? existingAddon.id : null, // store quotationAddon id
            };
         });

         setAddons(editable);
         setSelectedAddonIds(selectedIds);
         setOriginalSelectedAddonIds(selectedIds);
      } catch (err) {
         console.error('Failed to fetch services', err);
         setAddons([]);
         setSelectedAddonIds([]);
         toast.error('Failed to load services');
      } finally {
         setAddonLoading(false);
         setAddonOpen(true);
         // restore the preserved row so applyAddons can use it
         if (currentRow) setMenuRow(currentRow);
      }
   };

   const toggleAddonSelection = (id) => {
      setAddons((prev) => prev.map((a) => (a.id === id ? { ...a, selected: !a.selected } : a)));
      setSelectedAddonIds((prev) =>
         prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      );
   };

   const handleAddonTypeChange = (id, newType) => {
      setAddons((prev) => prev.map((a) => (a.id === id ? { ...a, editedType: newType } : a)));
   };

   const handleAddonValueChange = (id, newValue) => {
      setAddons((prev) => prev.map((a) => (a.id === id ? { ...a, editedValue: newValue } : a)));
   };

   const handleAddonRegularServiceChange = (id, newValue) => {
      setAddons((prev) =>
         prev.map((a) => (a.id === id ? { ...a, isRegularService: newValue } : a)),
      );
   };

   const applyAddons = async () => {
      const selected = addons
         .filter((a) => a.selected)
         .map((a) => ({
            addonId: a.id,
            type: a.editedType ?? a.type,
            value: Number(a.editedValue ?? a.value),
         }));
      if (selected.length === 0 && originalSelectedAddonIds.length === 0) {
         toast.error('Please select at least one add-on');
         return;
      }

      if (!menuRow || !menuRow.id) {
         toast.error('Quotation not selected');
         return;
      }

      // Double-check clientAccepted status before applying
      if (menuRow?.clientAccepted) {
         toast.error(
            'Cannot modify services for accepted quotations. Client has already accepted this quotation.',
         );
         return;
      }

      // Validate selected add-ons
      for (const addon of selected) {
         const numValue = Number(addon.value);
         if (isNaN(numValue)) {
            toast.error('Invalid value for service. Please enter a valid number.');
            return;
         }
         if (addon.type === 'percentage') {
            if (numValue < 0 || numValue > 100) {
               toast.error('Percentage service must be between 0 and 100');
               return;
            }
         } else if (addon.type === 'fixed') {
            if (numValue < 0) {
               toast.error('Fixed service amount cannot be negative');
               return;
            }
         }
      }

      setAddonLoading(true);
      try {
         const currentSelectedIds = addons.filter((a) => a.selected).map((a) => a.id);
         const addedIds = currentSelectedIds.filter((id) => !originalSelectedAddonIds.includes(id));
         const removedIds = originalSelectedAddonIds.filter(
            (id) => !currentSelectedIds.includes(id),
         );

         // Detect already-selected add-ons whose type or value changed (needs delete + re-add)
         const modifiedIds = originalSelectedAddonIds.filter((id) => {
            if (!currentSelectedIds.includes(id)) return false; // being removed, handled separately
            const item = addons.find((a) => a.id === id);
            if (!item) return false;
            return (
               String(item.editedType) !== String(item.originalType) ||
               String(item.editedValue) !== String(item.originalValue) ||
               Boolean(item.isRegularService) !== Boolean(item.originalIsRegularService)
            );
         });

         // First, delete removed/deselected add-ons AND modified ones (will be re-added)
         const idsToDelete = [...removedIds, ...modifiedIds];
         if (idsToDelete.length > 0) {
            // find corresponding quotationAddon ids and delete
            const deletePromises = idsToDelete.map(async (addonId) => {
               const item = addons.find((a) => a.id === addonId);
               const qaId = item?.existingQAId;
               if (qaId) {
                  try {
                     await axios.delete(`${API_URL}quotation/${menuRow.id}/addons/${qaId}`, {
                        headers: {
                           Authorization: `Bearer ${getAuthToken()}`,
                        },
                     });
                  } catch (delErr) {
                     console.error('Failed to delete quotation service', delErr);
                     // continue deleting others
                  }
               }
            });
            await Promise.all(deletePromises);
         }

         // Next, add newly selected add-ons AND re-add modified ones
         const idsToAdd = [...addedIds, ...modifiedIds];
         if (idsToAdd.length > 0) {
            const toAdd = addons
               .filter((a) => idsToAdd.includes(a.id))
               .map((a) => ({
                  addonId: a.id,
                  type: a.editedType ?? a.type,
                  value: Number(a.editedValue ?? a.value),
                  isRegularService: a.isRegularService ?? false,
               }));

            const payload = { quotationAddons: toAdd };
            const res = await axios.post(`${API_URL}quotation/${menuRow.id}/addons`, payload, {
               headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${getAuthToken()}`,
               },
            });

            if (!(res?.data?.statusCode === 200)) {
               const msg = res?.data?.message || 'Failed to apply services';
               toast.error(msg);
               setAddonLoading(false);
               return;
            }
         }

         toast.success('services updated successfully');
         setAddonOpen(false);
         refetch();
      } catch (err) {
         console.error('Error applying services', err);
         toast.error('Error updating services');
      } finally {
         setAddonLoading(false);
      }
   };

   const customStyles = {
      rows: {
         style: {
            margin: '0px',
            padding: 0,
         },
      },
      headCells: {
         style: {
            margin: '0px',
            padding: '10px 15px',
            fontWeight: 600,
            fontSize: 15,
            background: '#3F30FF1A',
         },
      },
      cells: {
         style: {
            margin: '0px',
            padding: '10px 15px',
            fontSize: 15,
         },
      },
   };

   useEffect(() => {
      const timer = setTimeout(() => {
         setSearchQuery(searchInput);
         setPage(1);
      }, 500);

      return () => clearTimeout(timer);
   }, [searchInput]);

   useEffect(() => {
      if (!isLoading && !isError) {
         setQuotationData(quotations?.response);
         setLoading(false);
      }
   }, [isLoading, isError, quotations]);

   const handleEdit = (id) => {
      navigate(`/quotation-edit/${id}`);
   };

   const handleSearch = (search) => {
      setSearchInput(search);
   };

   const handleDelete = async (id) => {
      const result = await MySwal.fire({
         title: 'Are you sure?',
         text: 'Are you sure you want to delete this quotation?',
         icon: 'warning',
         showCancelButton: true,
         confirmButtonText: 'Yes, delete it!',
         cancelButtonText: 'Cancel',
      });

      if (!result.isConfirmed) return;

      try {
         const response = await axios.post(
            `${API_URL}quotation/delete/${id}`,
            {},
            {
               headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${getAuthToken()}`,
               },
            },
         );

         if (response.status === 200) {
            await MySwal.fire('Deleted!', 'Quotation deleted successfully', 'success');
            refetch();
         } else {
            await MySwal.fire('Error', 'Failed to delete quotation', 'error');
         }
      } catch (error) {
         console.error('Error deleting quotation:', error);
         await MySwal.fire(
            'Denied!',
            error?.response?.data?.message || 'Error deleting quotation',
            'error',
         );
      }
   };

   const columns = [
      {
         name: 'SL No',
         cell: (row, index) => (page - 1) * rowsPerPage + index + 1,
         button: false,
      },
      // {
      //     name: 'Quotation No',
      //     selector: row => row.uniqueId,
      // },
      {
         name: 'Name',
         selector: (row) => row.title,
         sortable: true,
         width: '250px',
         wrap: true,
         cell: (row) => (
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{row.title}</div>
         ),
      },
      {
         name: 'Opportunity',
         selector: (row) => row.opportunity?.name || '—',
         sortable: false,
         width: '200px',
         wrap: true,
         cell: (row) => (
            <div
               style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}
            >
               {row.opportunity?.name || <span style={{ color: '#9ca3af' }}>—</span>}
            </div>
         ),
      },
      {
         name: 'Client',
         selector: (row) => row.clientName,
         sortable: true,
         width: '200px',
         wrap: true,
         cell: (row) => (
            <div
               style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}
            >
               {row.clientName}
            </div>
         ),
      },
      {
         name: 'Created By',
         selector: (row) => row.user && row.user?.name,
         sortable: true,
         wrap: true,
         cell: (row) => (
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
               {row.user && row.user?.name}
            </div>
         ),
      },
      {
         name: 'Date',
         selector: (row) => new Date(row.date).toDateString(),
         sortable: true,
         width: '140px',
         wrap: true,
         cell: (row) => (
            <div
               style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}
            >
               {new Date(row.date).toDateString()}
            </div>
         ),
      },
      {
         name: 'Dev Cost',
         selector: (row) => (
            <div>
               <b>
                  {row.currency?.symbol ?? 'USD'} {Number(row.total).toLocaleString()}
               </b>
            </div>
         ),
         sortable: true,
         sortField: 'total',
         width: '130px',
      },
      {
         name: 'Net Total',
         selector: (row) => (
            <div>
               <b>
                  {row.currency?.symbol ?? 'USD'} {Number(row.netTotal).toLocaleString()}
               </b>
            </div>
         ),
         sortable: true,
         width: '130px',
      },
      {
         name: 'PDF',
         selector: (row) => (
            <div>
               <Tooltip title="Download">
                  <Button
                     className="dark-link"
                     onClick={() => {
                        handleDownload(row.id, row.title);
                     }}
                  >
                     <CloudDownloadIcon />
                  </Button>
               </Tooltip>
            </div>
         ),
         width: '100px',
      },
      {
         name: 'Client PDF',
         selector: (row) => {
            const cds = row.clientDiscounts;
            const last = cds && cds.length > 0 ? cds[cds.length - 1] : null;
            return (
               <div>
                  {last && last.pdfPath ? (
                     <Tooltip title="Download">
                        <Link
                           className="dark-link"
                           to={API_URL + last.pdfPath}
                           target="_blank"
                           download
                        >
                           <CloudDownloadIcon />
                        </Link>
                     </Tooltip>
                  ) : (
                     <span style={{ color: '#6b7280' }}>N/A</span>
                  )}
               </div>
            );
         },
         width: '120px',
      },
      // {
      //     name: 'Status',
      //     selector: row => row.quotationStatus?.name ?? 'ON Holding'
      // },
      {
         name: 'Action',
         width: '100px',
         cell: (row) => (
            <div>
               <IconButton aria-label="more" onClick={(e) => openMenuForRow(e, row)}>
                  <MoreVertIcon />
               </IconButton>
               <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl) && menuRow?.id === row.id}
                  onClose={closeMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                     sx: { borderRadius: 2, boxShadow: 3, minWidth: 170 },
                  }}
               >
                  <MenuItem
                     onClick={() => {
                        closeMenu();
                        handleEdit(row.id);
                     }}
                     disabled={row.clientAccepted || !hasPermissions?.canUpdate}
                     sx={{ gap: 1 }}
                  >
                     <ListItemIcon>
                        <EditIcon fontSize="small" />
                     </ListItemIcon>
                     Edit
                  </MenuItem>
                  <MenuItem
                     disabled={!hasPermissions?.canDelete}
                     onClick={() => {
                        closeMenu();
                        handleDelete(row.id);
                     }}
                     sx={{ gap: 1 }}
                  >
                     <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                     </ListItemIcon>
                     Delete
                  </MenuItem>
                  <MenuItem
                     onClick={openDiscountModal}
                     disabled={row.clientAccepted}
                     sx={{ gap: 1 }}
                  >
                     <ListItemIcon>
                        <LocalOfferIcon fontSize="small" />
                     </ListItemIcon>
                     Discount
                  </MenuItem>
                  <MenuItem onClick={openAddonModal} disabled={row.clientAccepted} sx={{ gap: 1 }}>
                     <ListItemIcon>
                        <AddIcon fontSize="small" />
                     </ListItemIcon>
                     Services
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handlePreviewClick} sx={{ gap: 1 }}>
                     <ListItemIcon>
                        <VisibilityIcon fontSize="small" />
                     </ListItemIcon>
                     Preview
                  </MenuItem>
                  <MenuItem
                     onClick={handleSendClick}
                     disabled={sendLoading || row.clientAccepted}
                     sx={{ gap: 1 }}
                  >
                     <ListItemIcon>
                        <SendIcon fontSize="small" />
                     </ListItemIcon>
                     Send
                  </MenuItem>
                  <MenuItem onClick={handleLogsClick} sx={{ gap: 1 }}>
                     <ListItemIcon>
                        <HistoryIcon fontSize="small" />
                     </ListItemIcon>
                     Logs
                  </MenuItem>
               </Menu>
            </div>
         ),
         button: false,
      },
   ];

   if (loading) {
      return <Loading />;
   }

   const handleCloseRedirectDialog = () => {
      setRedirectOpen(false);
   };

   return (
      <>
         <Dialog open={redirectOpen} onClose={handleCloseRedirectDialog}>
            <DialogTitle>Quotation Expired</DialogTitle>

            <DialogContent>
               <Typography
                  variant="body1"
                  sx={{
                     textAlign: 'center',
                     py: 1,
                  }}
               >
                  This quotation has expired and can no longer be used.
                  <br />
                  Would you like to edit and renew it?
               </Typography>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
               <Button onClick={handleCloseRedirectDialog} variant="outlined">
                  Close
               </Button>

               <Button
                  variant="contained"
                  color="primary"
                  LinkComponent={Link}
                  to={`/quotation-edit/${menuRow?.id}`}
               >
                  Edit Quotation
               </Button>
            </DialogActions>
         </Dialog>
         <StyledDashboardLayout>
            <div className="content-wrapper">
               <div className="content-header">
                  <div className="container-fluid">
                     <div className="d-flex justify-content-between mb-2">
                        <div className="pageTitle">
                           <h1 className="m-0">Quotations</h1>
                        </div>
                        {hasPermissions?.canAdd && (
                           <div className="btnWrap">
                              <Link to="/quotation-add" className="btn addBtn">
                                 <PostAddIcon /> Add Quotation
                              </Link>
                           </div>
                        )}
                     </div>
                  </div>
               </div>
               <div className="card m-3 customCard">
                  {isError ? (
                     <Box
                        sx={{
                           display: 'flex',
                           flexDirection: 'column',
                           alignItems: 'center',
                           justifyContent: 'center',
                           py: 8,
                           gap: 2,
                           backgroundColor: '#fee',
                           borderRadius: 2,
                           border: '1px solid #fcc',
                           p: 4,
                        }}
                     >
                        <Typography
                           variant="body2"
                           sx={{ color: '#666', textAlign: 'center', maxWidth: 400 }}
                        >
                           {error?.data?.message ||
                              error?.message ||
                              'Something went wrong while fetching quotations. Please try again.'}
                        </Typography>
                     </Box>
                  ) : (
                     <div>
                        <Grid container spacing={2}>
                           <Grid item md={6} xs={12}></Grid>
                           <Grid item md={6} xs={12} display="flex" justifyContent="flex-end">
                              <Search onSearch={(query) => handleSearch(query)} />
                           </Grid>
                        </Grid>

                        <DataTable
                           columns={columns}
                           data={quotationData.data}
                           pagination
                           paginationServer
                           sortServer
                           onSort={(col, dir) => {
                              setSort({ sortBy: col.sortField || '', orderBy: dir.toUpperCase() });
                              setPage(1);
                           }}
                           paginationTotalRows={quotationData.pagination.total}
                           paginationDefaultPage={page}
                           paginationPerPage={rowsPerPage}
                           onChangePage={setPage}
                           onChangeRowsPerPage={(newPerPage) => {
                              setRowsPerPage(newPerPage);
                              setPage(1);
                           }}
                           customStyles={customStyles}
                           className="dataTable"
                           progressPending={isLoading}
                           progressComponent={
                              <Box sx={{ py: 4, textAlign: 'center' }}>
                                 <Typography>Loading data...</Typography>
                              </Box>
                           }
                           noDataComponent={
                              <Box
                                 sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    py: 6,
                                    gap: 1,
                                 }}
                              >
                                 <Typography variant="h6" sx={{ color: '#999' }}>
                                    No quotations found
                                 </Typography>
                                 <Typography variant="body2" sx={{ color: '#bbb' }}>
                                    Try adjusting your search criteria
                                 </Typography>
                              </Box>
                           }
                        />

                        {/* Discount selection dialog */}
                        <Dialog
                           open={discountOpen}
                           onClose={discountLoading ? undefined : () => setDiscountOpen(false)}
                           fullWidth
                           maxWidth="sm"
                        >
                           <DialogTitle sx={{ fontWeight: 600 }}>Select Discounts</DialogTitle>
                           <DialogContent
                              dividers
                              sx={{ maxHeight: '60vh', overflow: 'auto', py: 2 }}
                           >
                              {discountLoading ? (
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       flexDirection: 'column',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       py: 6,
                                       gap: 2,
                                    }}
                                 >
                                    <Box
                                       sx={{
                                          width: 50,
                                          height: 50,
                                          border: '4px solid #f3f3f3',
                                          borderTop: '4px solid #1976d2',
                                          borderRadius: '50%',
                                          animation: 'spin 1s linear infinite',
                                          '@keyframes spin': {
                                             '0%': { transform: 'rotate(0deg)' },
                                             '100%': {
                                                transform: 'rotate(360deg)',
                                             },
                                          },
                                       }}
                                    />
                                    <Typography
                                       variant="body1"
                                       sx={{ color: '#666', fontWeight: 500 }}
                                    >
                                       Loading discounts...
                                    </Typography>
                                 </Box>
                              ) : (
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       flexDirection: 'column',
                                       gap: 1,
                                    }}
                                 >
                                    {discounts.map((d) => (
                                       <Paper
                                          key={d.id}
                                          variant="outlined"
                                          sx={{
                                             display: 'flex',
                                             alignItems: 'center',
                                             gap: 2,
                                             p: 1.5,
                                             opacity: d.is_active === false ? 0.5 : 1,
                                             cursor:
                                                d.is_active === false ? 'not-allowed' : 'pointer',
                                             pointerEvents: d.is_active === false ? 'none' : 'auto',
                                          }}
                                       >
                                          <Box
                                             sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                pr: 1,
                                             }}
                                          >
                                             <Checkbox
                                                edge="start"
                                                checked={!!d.selected}
                                                onChange={() => toggleDiscountSelection(d.id)}
                                                disabled={d.is_active === false}
                                                tabIndex={-1}
                                                disableRipple
                                             />
                                          </Box>

                                          <Box sx={{ flex: 1, minWidth: 0 }}>
                                             {d.isCustom ? (
                                                <TextField
                                                   size="small"
                                                   fullWidth
                                                   label="Custom discount name"
                                                   value={d.name}
                                                   onChange={(e) =>
                                                      handleDiscountNameChange(d.id, e.target.value)
                                                   }
                                                   disabled={!d.selected}
                                                   inputProps={{ maxLength: 255 }}
                                                />
                                             ) : (
                                                <Typography
                                                   variant="subtitle1"
                                                   sx={{
                                                      fontWeight: 600,
                                                      whiteSpace: 'normal',
                                                      wordWrap: 'break-word',
                                                      overflowWrap: 'break-word',
                                                   }}
                                                >
                                                   {d.name}
                                                </Typography>
                                             )}
                                          </Box>

                                          <Box
                                             sx={{
                                                display: 'flex',
                                                gap: 2,
                                                alignItems: 'center',
                                                minWidth: 260,
                                             }}
                                          >
                                             <FormControl size="small" sx={{ minWidth: 140 }}>
                                                <InputLabel id={`type-label-${d.id}`}>
                                                   Type
                                                </InputLabel>
                                                <Select
                                                   labelId={`type-label-${d.id}`}
                                                   value={d.editedType}
                                                   label="Type"
                                                   onChange={(e) =>
                                                      handleDiscountTypeChange(d.id, e.target.value)
                                                   }
                                                   disabled={d.is_active === false || !d.selected}
                                                >
                                                   <MenuItem value="percentage">
                                                      Percentage
                                                   </MenuItem>
                                                   <MenuItem value="fixed">Fixed</MenuItem>
                                                </Select>
                                             </FormControl>

                                             <TextField
                                                size="small"
                                                type="number"
                                                label="Value"
                                                value={d.editedValue}
                                                onChange={(e) =>
                                                   handleDiscountValueChange(d.id, e.target.value)
                                                }
                                                disabled={d.is_active === false || !d.selected}
                                                InputProps={{
                                                   inputProps: { min: 0 },
                                                   endAdornment:
                                                      d.editedType === 'percentage' ? (
                                                         <InputAdornment position="end">
                                                            %
                                                         </InputAdornment>
                                                      ) : null,
                                                }}
                                                sx={{ width: 100 }}
                                             />
                                             {d.isCustom && !d.existingQDId && (
                                                <IconButton
                                                   size="small"
                                                   aria-label="Remove custom discount"
                                                   onClick={() => removeNewCustomDiscount(d.id)}
                                                >
                                                   <DeleteIcon fontSize="small" />
                                                </IconButton>
                                             )}
                                          </Box>
                                       </Paper>
                                    ))}

                                    {discounts.length === 0 && (
                                       <Typography>No discounts available</Typography>
                                    )}

                                    <Button
                                       startIcon={<AddIcon />}
                                       onClick={addCustomDiscount}
                                       sx={{ alignSelf: 'flex-start' }}
                                    >
                                       Add custom discount
                                    </Button>
                                 </Box>
                              )}
                           </DialogContent>
                           {!discountLoading && (
                              <DialogActions sx={{ px: 3, py: 2 }}>
                                 <Button onClick={() => setDiscountOpen(false)}>Cancel</Button>
                                 <Button onClick={applyDiscounts} variant="contained">
                                    Apply
                                 </Button>
                              </DialogActions>
                           )}
                        </Dialog>

                        {/* Service selection dialog */}
                        <Dialog
                           open={addonOpen}
                           onClose={addonLoading ? undefined : () => setAddonOpen(false)}
                           fullWidth
                           maxWidth="sm"
                        >
                           <DialogTitle sx={{ fontWeight: 600 }}>Select Services</DialogTitle>
                           <DialogContent
                              dividers
                              sx={{ maxHeight: '60vh', overflow: 'auto', py: 2 }}
                           >
                              {addonLoading ? (
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       flexDirection: 'column',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       py: 6,
                                       gap: 2,
                                    }}
                                 >
                                    <Box
                                       sx={{
                                          width: 50,
                                          height: 50,
                                          border: '4px solid #f3f3f3',
                                          borderTop: '4px solid #1976d2',
                                          borderRadius: '50%',
                                          animation: 'spin 1s linear infinite',
                                          '@keyframes spin': {
                                             '0%': { transform: 'rotate(0deg)' },
                                             '100%': {
                                                transform: 'rotate(360deg)',
                                             },
                                          },
                                       }}
                                    />
                                    <Typography
                                       variant="body1"
                                       sx={{ color: '#666', fontWeight: 500 }}
                                    >
                                       Loading services...
                                    </Typography>
                                 </Box>
                              ) : (
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       flexDirection: 'column',
                                       gap: 1,
                                    }}
                                 >
                                    {addons.map((addon) => (
                                       <Paper
                                          key={addon.id}
                                          variant="outlined"
                                          sx={{
                                             display: 'flex',
                                             alignItems: 'center',
                                             gap: 2,
                                             p: 1.5,
                                             opacity: addon.is_active === false ? 0.5 : 1,
                                             cursor:
                                                addon.is_active === false
                                                   ? 'not-allowed'
                                                   : 'pointer',
                                             pointerEvents:
                                                addon.is_active === false ? 'none' : 'auto',
                                          }}
                                       >
                                          {/* Checkbox Column */}
                                          <Box
                                             sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                pr: 1,
                                             }}
                                          >
                                             <Checkbox
                                                edge="start"
                                                checked={!!addon.selected}
                                                onChange={() => toggleAddonSelection(addon.id)}
                                                disabled={addon.is_active === false}
                                                tabIndex={-1}
                                                disableRipple
                                             />
                                          </Box>

                                          {/* Name Column */}
                                          <Box sx={{ flex: 1, minWidth: 0 }}>
                                             <Typography
                                                variant="subtitle1"
                                                sx={{
                                                   fontWeight: 600,
                                                   whiteSpace: 'normal',
                                                   wordWrap: 'break-word',
                                                   overflowWrap: 'break-word',
                                                }}
                                             >
                                                {addon.name}
                                             </Typography>
                                          </Box>

                                          {/* Type & Value Column */}
                                          <Box
                                             sx={{
                                                display: 'flex',
                                                gap: 2,
                                                alignItems: 'center',
                                                minWidth: 260,
                                             }}
                                          >
                                             <FormControl size="small" sx={{ minWidth: 140 }}>
                                                <InputLabel id={`addon-type-label-${addon.id}`}>
                                                   Type
                                                </InputLabel>
                                                <Select
                                                   labelId={`addon-type-label-${addon.id}`}
                                                   value={addon.editedType}
                                                   label="Type"
                                                   onChange={(e) =>
                                                      handleAddonTypeChange(
                                                         addon.id,
                                                         e.target.value,
                                                      )
                                                   }
                                                   disabled={
                                                      addon.is_active === false || !addon.selected
                                                   }
                                                >
                                                   <MenuItem value="percentage">
                                                      Percentage
                                                   </MenuItem>
                                                   <MenuItem value="fixed">Fixed</MenuItem>
                                                </Select>
                                             </FormControl>

                                             <TextField
                                                size="small"
                                                type="number"
                                                label="Value"
                                                value={addon.editedValue}
                                                onChange={(e) =>
                                                   handleAddonValueChange(addon.id, e.target.value)
                                                }
                                                disabled={
                                                   addon.is_active === false || !addon.selected
                                                }
                                                InputProps={{
                                                   inputProps: { min: 0 },
                                                   endAdornment:
                                                      addon.editedType === 'percentage' ? (
                                                         <InputAdornment position="end">
                                                            %
                                                         </InputAdornment>
                                                      ) : null,
                                                }}
                                                sx={{ width: 100 }}
                                             />
                                          </Box>

                                          {/* Regular Service Toggle */}
                                          <Box
                                             sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.8,
                                             }}
                                          >
                                             <Switch
                                                size="small"
                                                checked={!!addon.isRegularService}
                                                onChange={(e) =>
                                                   handleAddonRegularServiceChange(
                                                      addon.id,
                                                      e.target.checked,
                                                   )
                                                }
                                                disabled={
                                                   addon.is_active === false || !addon.selected
                                                }
                                             />
                                             <Typography
                                                variant="caption"
                                                sx={{
                                                   fontSize: '12px',
                                                   fontWeight: 500,
                                                   color: '#666',
                                                   whiteSpace: 'nowrap',
                                                }}
                                             >
                                                Regular
                                             </Typography>
                                          </Box>
                                       </Paper>
                                    ))}

                                    {addons.length === 0 && (
                                       <Typography>No services available</Typography>
                                    )}
                                 </Box>
                              )}
                           </DialogContent>
                           {!addonLoading && (
                              <DialogActions sx={{ px: 3, py: 2 }}>
                                 <Button onClick={() => setAddonOpen(false)}>Cancel</Button>
                                 <Button onClick={applyAddons} variant="contained">
                                    Apply
                                 </Button>
                              </DialogActions>
                           )}
                        </Dialog>

                        {/* Preview Modal */}
                        <QuotationPreviewModal
                           open={previewOpen}
                           quotationId={menuRow?.id}
                           onClose={() => setPreviewOpen(false)}
                        />

                        {/* Logs Modal */}
                        <QuotationLogsModal
                           open={logsOpen}
                           quotationId={menuRow?.id}
                           onClose={() => setLogsOpen(false)}
                        />
                     </div>
                  )}
               </div>
            </div>
         </StyledDashboardLayout>
      </>
   );
}

export default QuotationList;
